import type {
  EventDetail,
  EventListQuery,
  EventListResponse,
  EventUpdateInput,
  SeenResponse,
} from "@cdt/shared";

export type BulkPriorityResult = { updated: number };

import { api } from "@/lib/api";
import { queryToSearchParams } from "@/lib/eventsQueryParams";

export async function fetchEvents(
  query: EventListQuery,
): Promise<EventListResponse> {
  const sp = queryToSearchParams(query);
  const qs = sp.toString();
  const path = qs.length > 0 ? `/api/events?${qs}` : "/api/events";
  return api.get<EventListResponse>(path);
}

export async function fetchEvent(id: string): Promise<EventDetail> {
  return api.get<EventDetail>(`/api/events/${encodeURIComponent(id)}`);
}

export async function updateEvent(
  id: string,
  patch: EventUpdateInput,
): Promise<EventDetail> {
  return api.patch<EventDetail>(
    `/api/events/${encodeURIComponent(id)}`,
    patch,
  );
}

export async function approveEvent(id: string): Promise<EventDetail> {
  return api.post<EventDetail>(
    `/api/events/${encodeURIComponent(id)}/approve`,
  );
}

export async function rejectEvent(
  id: string,
  reason?: string,
): Promise<EventDetail> {
  const body: { reason?: string } = {};
  if (reason && reason.trim().length > 0) body.reason = reason.trim();
  return api.post<EventDetail>(
    `/api/events/${encodeURIComponent(id)}/reject`,
    body,
  );
}

export async function retryEvent(id: string): Promise<EventDetail> {
  return api.post<EventDetail>(`/api/events/${encodeURIComponent(id)}/retry`);
}

export async function setEventPriority(
  id: string,
  priority: number,
): Promise<EventDetail> {
  return api.patch<EventDetail>(
    `/api/events/${encodeURIComponent(id)}/priority`,
    { priority },
  );
}

export async function bulkSetPriority(
  ids: string[],
  priority: number,
): Promise<BulkPriorityResult> {
  return api.patch<BulkPriorityResult>("/api/events/priority/bulk", {
    ids,
    priority,
  });
}

export async function deleteEvent(id: string): Promise<EventDetail> {
  return api.delete<EventDetail>(`/api/events/${encodeURIComponent(id)}`);
}

export async function markEventSeen(id: string): Promise<SeenResponse> {
  return api.post<SeenResponse>(`/api/events/${encodeURIComponent(id)}/seen`);
}

export async function markEventUnseen(id: string): Promise<SeenResponse> {
  return api.post<SeenResponse>(`/api/events/${encodeURIComponent(id)}/unseen`);
}

export async function markAllEventsSeen(): Promise<{ updated: number }> {
  return api.post<{ updated: number }>("/api/events/seen/all");
}

export async function clearManualReviewQueue(): Promise<{ updated: number }> {
  return api.post<{ updated: number }>("/api/events/manual-review/clear");
}
